<script setup>

import {useStripe} from "~/composables/useStripe.js";
import * as yup from "yup";

const props = defineProps({
  id: {
    type: String,
    default: null,
  },
  formTitle: {
    type: String,
    required: true,
  },
  stripeKey: {
    type: String,
    required: true,
  },
  price: {
    type: String,
    required: true,
  },
  formFields: {
    type: String,
    required: true,
  },
  salesforceCampaignId: {
    type: String,
    required: true,
  },
  isPaymentEnabled: {
    type: String,
    required: true,
  }
});

const contactId = ref('');
const appearance = {
  theme: 'night',
  // labels: 'floating',
  variables: {
    colorPrimary: '#0570de',
    colorBackground: '#130725',
    colorText: '#ffffff',
    colorDanger: '#f2287d',
    fontFamily: 'Euclid Galactic, sans-serif',
    borderRadius: '16px',
    iconColor: '#7c55b8',
    colorTextPlaceholder: 'rgba(255, 255, 255, 0.3)',
  },

  rules: {
    '.Label': {
      color: '#ffffff',
      textTransform: 'uppercase',
      fontSize: '10px',
      letterSpacing: '0.27em',
      fontWeight: '600',
      marginLeft: '22px',
      marginBottom: '8px',
    },
    '.Input': {
      boxShadow: '0px',
      padding: '18px 22px',
      borderColor: '#1d083a',
    },
    '.Input:focus': {
      borderColor: '#c80afb',
      boxShadow: '0px',
    },
  },
}
const isSubmitting = ref(false)
const fonts = [
  {
    family: 'Euclid Galactic',
    src: 'url(https://www.virgingalactic.com/assets/fonts/EuclidGalactic-2G-WebXL.woff2)',
    weight: '400',
  },
  {
    family: 'Euclid Galactic',
    src: 'url(https://www.virgingalactic.com/assets/fonts/EuclidGalactic-3G-WebXL.woff2)',
    weight: '500',
  },
]
const formTitle = ref(props.formTitle);
const runtimeConfig = useRuntimeConfig()
const baseURL = runtimeConfig.STRIPE_WORKER_URI;
const postUrl = `${baseURL}/client/token`;
const isSubmitted = ref(false);
const isPaymentEnabled = ref(props.isPaymentEnabled === true);
const thankYouMessage = ref(props.isPaymentEnabled === true ? 'Payment was successful!' : 'For your submission.');
let stripeData
const formFields = reactive({...props.formFields.fields});
const errors = reactive({})
const hasErrors = computed(() => {
  return Object.keys(errors).length > 0;
});

const schema = yup.object({
  value: yup
    .string().test('test-length', 'Cannot be empty',
(value) => {
 return value && value.trimStart().length >= 2
}).required(),
});
const validateInput = async (event, field) => {
    await validateField(field);
}

const validateField = async (field) => {

  if (field && field.required) {
    try {
      console.log('validating value: ', field);
      await schema.validateAt('value', field);
      delete errors[field.label];
      console.log('is valid', field)
    } catch (ex) {
      console.log('failed to validate: ', ex);
      errors[field.label] = true;
    }
  }
}

const payload = {
  price: props.price,
};
const route = useRoute()
onMounted(async () => {
  if (!isPaymentEnabled.value) {
    console.log('Payment is not enabled');
    return;
  }
  try {
    console.log('posting data: ', payload);
    const response = await fetch(`${baseURL}/token/generate`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (response) {
      const data = await response.json();
      console.log('generated token: ', data);
      stripeData = await useStripe(data.client_secret, props.stripeKey, appearance)
      console.log('stripe return: ', stripeData);
    }

  } catch (ex) {
    console.log('failed to get data: ', ex);
  }
})

const handleCountrySelectorChanged = async (field, value, selectedField) => {
  selectedField.value = value;
}
const handleSubmit = async () => {

  isSubmitting.value = true;

  if (isPaymentEnabled.value &&  stripeData) {
    const stripeElements = await stripeData.elements.submit();

    if (stripeElements.error) {
      isSubmitting.value = false;
      return;
    }
  }

  const payload = {
    campaignId : props.salesforceCampaignId,
    data : [],
    price: props.price,
  }

  for (let formFieldsKey in formFields) {
    await validateField(formFields[formFieldsKey]);
  }

  if (hasErrors.value) {
    console.log('form has errors')
    isSubmitting.value = false;
    return;
  }

  for (let formFieldsKey in formFields) {
    payload.data.push(formFields[formFieldsKey]);
  }

  const response = await fetch(`${baseURL}/contact/create`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const contactInfo = await response.json();

  if (!isPaymentEnabled.value) {
    isSubmitting.value = false;
    isSubmitted.value = true;
    return;
  }

  if (contactInfo && contactInfo.contactId) {
    const stripeResponse = await stripeData.stripe.confirmPayment({
      elements: stripeData.elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: 'https://virgingalactic.com',
      },
    });

    console.log('sunrise: ', 'stripe response ', stripeResponse);

    const { error, paymentIntent } = stripeResponse;

    const attendeeData = {
      contactId: contactInfo.contactId,
      transaction_id: paymentIntent.id,
      transaction_amount: paymentIntent.amount,
      transaction_status: paymentIntent.status,
      transaction_timestamp: paymentIntent.created,
    };
    const confirmationResponse = await fetch(`${baseURL}/contact/update`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(attendeeData),
    });

    const submissionResponse = await confirmationResponse.json();

    if (submissionResponse && submissionResponse.success) {
      isSubmitted.value = true;
    }

    console.log('contact updated: ', submissionResponse);
  }

  isSubmitting.value = false;
}

const handleSelectChange = (e, field) => {
  let id = e.target.value;
  let name = e.target.options[e.target.options.selectedIndex].text;

  field.selected = name;
  field.value = name;
}


</script>

<template>
  <section class="salesforce-sign-up-module data-module" :id='id'>
    <div class="text-center" v-if="isSubmitted">
      <h2>Thank You!</h2>
      <p>{{thankYouMessage}}</p>
    </div>
    <div v-else>
      <div class="form-container">
        <div class="title" v-if="formTitle">
          <h2>{{ formTitle }}</h2>
          <div class="form__divider" ></div>
        </div>
        <div v-for="(field, index) in formFields" :key="index">

          <input v-if="field.type === 'text' || field.type === 'email'" autocomplete="off"
                 :type="field.type"
                 required
                 @blur="validateInput($event, field)"
                 :class="['input__text', 'capitalize', { 'has-error': errors[field.label] }]"
                 :placeholder="field.label"
                 v-model="field.value"
          />

          <input v-if="field.type === 'date'" autocomplete="off"
                 onfocus="(this.type='date')"
                 onblur="(this.type='text')"
                 required
                 @blur="validateInput($event, field)"
                 :class="['input__text', 'capitalize', { 'has-error': errors[field.label] }]"
                 :placeholder="field.label"
                 v-model="field.value"
          />

          <div :class="['input-select']" v-if="field.type === 'select'">
            <select
              class="input-select__select"
              @change="handleSelectChange($event, field)"
            >
              <option disabled>Select {{ field.label }}</option>
              <option
                v-for="option in field.options"
                :key="option"
                :value="option"
                :selected="option === field.selected"
              >
                {{ option }}
              </option>
            </select>

            <div class="input-select__label">
              {{ field.selected || field.label }}
            </div>
          </div>
          <div class="form__form-row">
            <InputNationality  v-if="field.type === 'country_select'"
                               :custom-field="field"
                               :selected="field.value"
                               @on-selection-changed="handleCountrySelectorChanged"
            />
          </div>
        </div>
      </div>
      <div v-if="isPaymentEnabled">
        <div id="checkout"></div>
      </div>
      <div>
        <ButtonPill v-if="isSubmitting" class="nxtgen__submit" big disabled>
          Submitting
        </ButtonPill>
        <ButtonPill
          v-else
          :disabled="hasErrors"
          class="nxtgen__submit"
          icon="chevron-right"
          big
          animate
          useGradient
          @click="handleSubmit"
        >
          {{route.path == '/nxtgen-sign-up' ? 'Submit' : 'Next'}}
        </ButtonPill>
      </div>
    </div>

  </section>

</template>
<style lang="scss">
@use 'sass:math';
@import 'styles/vendors/sass-mq/mq';
@import 'styles/utils/functions';
.data-module {
  position: relative;
  margin: 0 auto;
  width: 100%;

  @include mq($from: 'm') using($from) {
    max-width: gs(5, 4, 'xxxs');
  }
}

.capitalize {
  &--placeholder {
    text-transform: capitalize;
  }
}
.capitalize::placeholder, .salesforce-sign-up-module .input__search::placeholder {
  text-transform: capitalize;
  font-size: 0.8rem;
  font-weight: 400;
  letter-spacing: 0.24em;
}
.salesforce-sign-up-module .input-select__label {
  font-size: 0.8rem;
  font-weight: 400;
  letter-spacing: 0.24em;
  text-transform: capitalize;

 }

.input-select {
  position: relative;
  padding: 0;

  &__label {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Euclid Galactic', sans-serif;

    letter-spacing: -0.01em;
    font-size: 15px;
    line-height: 22px;
    font-weight: $font-weight-2G;
    text-align: center;
    color: rgba($brand-white, 0.9);
    background-color: #200929;
    border: solid 1.5px #1d083a;
    border-radius: 32px;
    box-shadow: none;
    // padding-top: 21px;
    // padding-bottom: 20px;
    width: 100%;
    height: 60px;
    position: relative;
    outline: none;

    background: url('assets/images/icons/chevron-down--purple.svg'), #200929;
    background-origin: border-box;
    background-size: 12px, auto;
    background-repeat: no-repeat !important;
    background-position: calc(100% - 24px) center, center;
    padding-inline-start: 0px;
    appearance: none;
    cursor: pointer;

    @include mq($from: 'l') using($from) {
      line-height: math.div(16, 11);
      // padding-top: 22px;
      // padding-bottom: 21px;
    }
  }

  &__select {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    right: 0;
    appearance: none;

    width: 100%;
    opacity: 0;
    z-index: 1;

    &:active + .input-select__label,
    &:focus + .input-select__label {
      border-color: transparent;
      background-image: url('assets/images/icons/chevron-down--purple.svg'),
      radial-gradient(
          200px 250px at 50% 50%,
          rgba(#c80afb, 0.1) 0%,
          rgba(200, 10, 251, 0.063) 31.77%,
          rgba(200, 10, 251, 0.036) 60.94%,
          rgba(200, 10, 251, 0) 99.94%
      ),
      linear-gradient(#200929, #200929),
      linear-gradient(103.2deg, #d90afb -13.13%, #31afd4 93.9%);
      background-origin: border-box;
      background-clip: padding-box, padding-box, padding-box, border-box;
      background-size: 12px, auto, auto, auto;
      background-position: calc(100% - 24px) center, center, center, center;
    }
  }
  .input-select__label {
    color: #936dcd;
  }

  &--placeholder {
    .input-select__label {
      color: #936dcd;
      font-size: 11px;
      font-weight: $font-weight-4G;
      line-height: math.div(12, 11);
      letter-spacing: 0.24em;
      text-transform: capitalize;
    }
  }

  &.has-error {
    .input-select__label {
      border-color: transparent;
      background-image: url('assets/images/icons/chevron-down--purple.svg'),
      radial-gradient(
          130px 270px at 50% 50%,
          rgba(#c80afb, 0.05) 0%,
          rgba(200, 10, 251, 0.0315) 31.77%,
          rgba(200, 10, 251, 0.018) 60.94%,
          rgba(200, 10, 251, 0) 99.94%
      ),
      linear-gradient(#200929, #200929), linear-gradient(#f2287d, #f2287d);
      background-origin: border-box;
      background-clip: padding-box, padding-box, padding-box, border-box;
      background-size: 12px, auto, auto, auto;
      background-position: calc(100% - 24px) center, center, center, center;
    }
  }
}
</style>
